// frontend/src/components/simple/SimpleDashboard.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { connectSocket, joinAssociation, onAssociationUpdate, onAssociationControl, onAutomationAction } from '../../services/socket';
import Card from '../common/Card';
import Loading from '../common/Loading';
import AcControlPanel from '../admin/AcControlPanel';
import SchedulePeriodsManager from '../common/SchedulePeriodsManager';

const SimpleDashboard = () => {
  const navigate = useNavigate();
  const [association, setAssociation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [controlling, setControlling] = useState(false);
  const [switchState, setSwitchState] = useState(null);
  const [sensorState, setSensorState] = useState(null);

  const fetchMyAssociation = useCallback(async () => {
    try {
      const response = await api.get('/simple-association/my-association');
      const data = response.data.data;
      setAssociation(data);

      // ✅ switchIsOn est désormais calculé côté backend en fonction du type
      //    d'actuateur (switch ou ir_controller) — fiable dans les deux cas.
      if (typeof data.switchIsOn === 'boolean') {
        setSwitchState(data.switchIsOn ? 'on' : 'off');
      } else {
        setSwitchState(prev => prev || 'unknown');
      }

      // --- Capteur ---
      const sensorStatusArray = data.realtimeStatus?.sensor || [];
      if (sensorStatusArray.length > 0) {
        const pirData = sensorStatusArray.find(
          s => s.code === 'pir' || s.code === 'presence_state' || s.code === 'presence'
        );
        if (pirData) {
          const value = typeof pirData.value === 'string' ? pirData.value.toLowerCase() : pirData.value;
          if (value === 'presence' || value === 'pir') setSensorState('presence');
          else if (value === 'move' || value === 'motion') setSensorState('move');
          else if (value === 'none' || value === 'absence' || value === false) setSensorState('none');
          else setSensorState(prev => prev || 'unknown');
        } else {
          setSensorState(prev => prev || 'unknown');
        }
      } else {
        setSensorState(prev => prev || 'unknown');
      }

    } catch (error) {
      console.error('❌ Erreur:', error);
      setSwitchState(prev => prev || 'unknown');
      setSensorState(prev => prev || 'unknown');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyAssociation();
    const interval = setInterval(fetchMyAssociation, 30000);
    return () => clearInterval(interval);
  }, [fetchMyAssociation]);

  // ✅ SOCKET.IO - Temps réel
  useEffect(() => {
    if (!association?._id) return;

    const socket = connectSocket();
    if (!socket) return;

    joinAssociation(association._id);

    // Écouter les mises à jour (autoMode changé par Owner/Admin, ET presence/switch
    // poussés en temps réel par le flux Pulsar — cf. tuyaPulsarService.js)
    const cleanupUpdate = onAssociationUpdate((data) => {
      if (data.associationId === association._id) {
        if (data.autoMode !== undefined) {
          setAssociation(prev => prev ? { ...prev, autoMode: data.autoMode } : prev);
        }
        if (data.presenceValue !== undefined) {
          setSensorState(data.presenceValue);
        }
        if (data.switchIsOn !== undefined) {
          setSwitchState(data.switchIsOn === true || data.switchIsOn === 'true' || data.switchIsOn === 1 ? 'on' : 'off');
        }
        if (data.schedulePeriods !== undefined) {
          setAssociation(prev => prev ? { ...prev, schedulePeriods: data.schedulePeriods } : prev);
        }
        if (data.schedule !== undefined) {
          setAssociation(prev => prev ? { ...prev, schedule: data.schedule } : prev);
        }
      }
    });

    // Écouter les contrôles manuels (par Owner)
    const cleanupControl = onAssociationControl((data) => {
      if (data.associationId === association._id) {
        setSwitchState(data.switchState);
      }
    });

    // Écouter l'automation (changement par le système)
    const cleanupAutomation = onAutomationAction((data) => {
      if (data.associationId === association._id) {
        setSwitchState(data.switchState);
      }
    });

    return () => {
      cleanupUpdate();
      cleanupControl();
      cleanupAutomation();
    };
  }, [association?._id]);

  const handleControl = async (action) => {
    if (controlling) return;

    setControlling(true);
    const previousState = switchState;
    setSwitchState(action);

    try {
      await api.post('/simple-association/control', { action });
      setTimeout(fetchMyAssociation, 3000);
    } catch (error) {
      setSwitchState(previousState);
      alert(error.response?.data?.message || 'Erreur lors du contrôle');
    } finally {
      setControlling(false);
    }
  };

  const handleToggleAutoMode = async () => {
    try {
      const newMode = !association.autoMode;
      // ✅ Optimistic update
      setAssociation(prev => prev ? { ...prev, autoMode: newMode } : prev);

      await api.patch('/simple-association/auto-mode', { enabled: newMode });
      // Pas besoin de fetchMyAssociation - le socket notifiera l'Owner
    } catch (error) {
      // Rollback
      setAssociation(prev => prev ? { ...prev, autoMode: !prev.autoMode } : prev);
      alert(error.response?.data?.message || 'Erreur');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Mon Dashboard
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                Contrôlez votre association en toute simplicité
              </p>
            </div>
            {association && (
              <div className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-2 ${
                association.active 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  association.active ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`}></span>
                {association.active ? 'Actif' : 'Inactif'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {association ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Colonne gauche - Contrôles */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Card Consommation (switch) OU résumé Climatisation (ir_controller) */}
              {association.switchId?.type === 'ir_controller' ? (
                <Card className="bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-500 text-white border-0 overflow-hidden relative">
                  <div className="absolute -right-8 -top-8 text-9xl opacity-10">🌡️</div>
                  <div className="relative z-10 p-4 sm:p-6">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <span className="text-4xl sm:text-5xl">🌡️</span>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">Climatisation</h2>
                    </div>
                    <p className="text-blue-100 text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
                      Contrôleur infrarouge — pilotage à distance
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3">
                        <p className="text-xs sm:text-sm text-blue-100 mb-1">Activations</p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold">
                          {association.statistics?.totalActivations || 0}
                        </p>
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3">
                        <p className="text-xs sm:text-sm text-blue-100 mb-1">Mode auto</p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold">
                          {association.autoMode ? 'Activé' : 'Désactivé'}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              ) : (
                <div 
                  className="group cursor-pointer transform transition-all duration-300 hover:scale-[1.02]"
                  onClick={() => navigate('/my-consumption')}
                >
                  <Card className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 text-white border-0 overflow-hidden relative">
                    <div className="absolute -right-8 -top-8 text-9xl opacity-10">⚡</div>
                    <div className="relative z-10 p-4 sm:p-6">
                      <div className="flex items-start justify-between mb-4 sm:mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <span className="text-4xl sm:text-5xl">📊</span>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">Ma Consommation</h2>
                          </div>
                          <p className="text-green-100 text-sm sm:text-base md:text-lg">
                            Suivez votre consommation en temps réel
                          </p>
                        </div>
                        <div className="hidden sm:block bg-white/20 backdrop-blur-sm rounded-full p-3 sm:p-4 group-hover:bg-white/30 transition-colors">
                          <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 sm:gap-4">
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3">
                          <p className="text-xs sm:text-sm text-green-100 mb-1">Coût ce mois</p>
                          <p className="text-lg sm:text-xl md:text-2xl font-bold">
                            {association.currentMonthCost ? `${association.currentMonthCost.toLocaleString()} FCFA` : '0 FCFA'}
                          </p>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3">
                          <p className="text-xs sm:text-sm text-green-100 mb-1">Activations</p>
                          <p className="text-lg sm:text-xl md:text-2xl font-bold">
                            {association.statistics?.totalActivations || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {association.switchId?.type === 'ir_controller' ? (
                <Card className="bg-white border-2 border-gray-100 shadow-lg">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-2 sm:p-3 rounded-xl">
                        <span className="text-2xl sm:text-3xl">🌡️</span>
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Climatisation</h2>
                        <p className="text-gray-600 text-xs sm:text-sm">
                          {association.switchId?.name}
                        </p>
                      </div>
                    </div>

                    <AcControlPanel endpointBase="/simple-association" associationName={association.name} />

                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                      <button
                        onClick={handleToggleAutoMode}
                        className={`w-full px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:-translate-y-0.5 ${
                          association.autoMode
                            ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg hover:shadow-xl'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          {association.autoMode ? (
                            <>
                              <span className="animate-pulse">🤖</span>
                              Mode Automatique: ACTIVÉ
                            </>
                          ) : (
                            <>
                              🔧 Mode Automatique: DÉSACTIVÉ
                            </>
                          )}
                        </span>
                      </button>
                    </div>
                  </div>
                </Card>
              ) : (
              <Card className="bg-white border-2 border-gray-100 shadow-lg">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-2 sm:p-3 rounded-xl">
                      <span className="text-2xl sm:text-3xl">🎮</span>
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Contrôles</h2>
                      <p className="text-gray-600 text-xs sm:text-sm">
                        {association.switchId?.name}
                      </p>
                    </div>
                  </div>

                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium text-sm sm:text-base">État actuel</span>
                      <div className="flex items-center gap-2 sm:gap-3">
                        {switchState === 'on' && (
                          <span className="flex items-center gap-2 text-green-600 font-semibold text-sm sm:text-base">
                            <span className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full animate-pulse"></span>
                            Allumé
                          </span>
                        )}
                        {switchState === 'off' && (
                          <span className="flex items-center gap-2 text-gray-600 font-semibold text-sm sm:text-base">
                            <span className="w-2 h-2 sm:w-3 sm:h-3 bg-gray-400 rounded-full"></span>
                            Éteint
                          </span>
                        )}
                        {switchState === 'unknown' && (
                          <span className="flex items-center gap-2 text-orange-600 font-semibold text-sm sm:text-base">
                            <span className="w-2 h-2 sm:w-3 sm:h-3 bg-orange-400 rounded-full animate-pulse"></span>
                            Inconnu
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <button
                      onClick={() => handleControl('on')}
                      disabled={controlling || !association.switchId?.online || switchState === 'on'}
                      className={`group relative overflow-hidden px-4 sm:px-6 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                        switchState === 'on'
                          ? 'bg-green-500 text-white border-4 border-green-600 shadow-green-200'
                          : 'bg-gradient-to-r from-green-400 to-emerald-500 text-white hover:from-green-500 hover:to-emerald-600'
                      }`}
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {controlling && switchState !== 'on' ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            <span className="hidden sm:inline">Allumage...</span>
                          </>
                        ) : (
                          <>
                            {switchState === 'on' ? '✅' : '🔆'}
                            {switchState === 'on' ? 'ALLUMÉ' : 'ALLUMER'}
                          </>
                        )}
                      </span>
                    </button>
                    
                    <button
                      onClick={() => handleControl('off')}
                      disabled={controlling || !association.switchId?.online || switchState === 'off'}
                      className={`group relative overflow-hidden px-4 sm:px-6 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                        switchState === 'off'
                          ? 'bg-gray-400 text-white border-4 border-gray-500 shadow-gray-200'
                          : 'bg-gradient-to-r from-red-400 to-rose-500 text-white hover:from-red-500 hover:to-rose-600'
                      }`}
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {controlling && switchState !== 'off' ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            <span className="hidden sm:inline">Extinction...</span>
                          </>
                        ) : (
                          <>
                            {switchState === 'off' ? '⭕' : '🌙'}
                            {switchState === 'off' ? 'ÉTEINT' : 'ÉTEINDRE'}
                          </>
                        )}
                      </span>
                    </button>
                  </div>

                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                    <button
                      onClick={handleToggleAutoMode}
                      className={`w-full px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:-translate-y-0.5 ${
                        association.autoMode
                          ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg hover:shadow-xl'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        {association.autoMode ? (
                          <>
                            <span className="animate-pulse">🤖</span>
                            Mode Automatique: ACTIVÉ
                          </>
                        ) : (
                          <>
                            🔧 Mode Automatique: DÉSACTIVÉ
                          </>
                        )}
                      </span>
                    </button>
                  </div>
                </div>
              </Card>
              )}
            </div>

            {/* Colonne droite */}
            <div className="space-y-4 sm:space-y-6">
              <Card className="bg-gradient-to-br from-purple-500 to-blue-500 text-white border-0">
                <div className="p-4 sm:p-6">
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">{association.name}</h3>
                  
                  {sensorState && (
                    <div className={`mb-3 sm:mb-4 p-3 rounded-lg ${
                      sensorState === 'presence' 
                        ? 'bg-red-500/30 border-2 border-red-300' 
                        : sensorState === 'move'
                        ? 'bg-yellow-500/30 border-2 border-yellow-300'
                        : sensorState === 'none'
                        ? 'bg-green-500/30 border-2 border-green-300'
                        : 'bg-orange-500/30 border-2 border-orange-300'
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {sensorState === 'presence' ? '👤' : 
                           sensorState === 'move' ? '🏃' : 
                           sensorState === 'none' ? '🚫' : '❓'}
                        </span>
                        <div>
                          <p className="text-xs font-semibold">État du capteur</p>
                          <p className="text-sm font-bold">
                            {sensorState === 'presence' ? 'PRÉSENCE DÉTECTÉE' : 
                             sensorState === 'move' ? 'MOUVEMENT DÉTECTÉ' : 
                             sensorState === 'none' ? 'AUCUNE PRÉSENCE' : 
                             'INCONNU'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {association.description && (
                    <p className="text-purple-100 mb-3 sm:mb-4 text-sm sm:text-base">{association.description}</p>
                  )}
                  
                  {association.schedule?.enabled && (
                    <div className="mt-3 sm:mt-4 p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                      <p className="text-xs font-semibold mb-1">Horaires programmés</p>
                      {association.schedulePeriods?.length > 0 ? (
                        <p className="text-sm">
                          {association.schedulePeriods.length} période{association.schedulePeriods.length > 1 ? 's' : ''} configurée{association.schedulePeriods.length > 1 ? 's' : ''}
                        </p>
                      ) : (
                        <p className="text-sm">
                          {association.schedule?.onTime} - {association.schedule?.offTime}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              <Card title="📅 Programmation horaire">
                <div className="p-3 sm:p-4">
                  <SchedulePeriodsManager
                    scheduleEnabled={association.schedule?.enabled || false}
                    onToggleEnabled={async (enabled) => {
                      await api.patch('/simple-association/schedule-enabled', { enabled });
                      await fetchMyAssociation();
                    }}
                    periods={association.schedulePeriods || []}
                    legacyOnTime={association.schedule?.onTime}
                    legacyOffTime={association.schedule?.offTime}
                    periodsBasePath="/simple-association/schedule-periods"
                    onPeriodsChange={() => fetchMyAssociation()}
                  />
                </div>
              </Card>

              <Card title="📱 Mes Appareils">
                <div className="p-3 sm:p-4 space-y-3">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="bg-blue-500 p-2 rounded-lg">
                        <span className="text-lg sm:text-xl">📡</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-blue-600 font-semibold uppercase">Capteur</p>
                        <p className="font-bold text-gray-800 text-sm truncate">{association.sensorId?.name}</p>
                      </div>
                    </div>
                    {association.sensorId?.online ? (
                      <div className="flex items-center gap-2 text-green-600 text-xs">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        <span className="font-semibold">En ligne</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-600 text-xs">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        <span className="font-semibold">Hors ligne</span>
                      </div>
                    )}
                  </div>

                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="bg-amber-500 p-2 rounded-lg">
                        <span className="text-lg sm:text-xl">{association.switchId?.type === 'ir_controller' ? '🌡️' : '💡'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-amber-600 font-semibold uppercase">
                          {association.switchId?.type === 'ir_controller' ? 'Contrôleur IR' : 'Interrupteur'}
                        </p>
                        <p className="font-bold text-gray-800 text-sm truncate">{association.switchId?.name}</p>
                      </div>
                    </div>
                    {association.switchId?.online ? (
                      <div className="flex items-center gap-2 text-green-600 text-xs">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        <span className="font-semibold">En ligne</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-600 text-xs">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        <span className="font-semibold">Hors ligne</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <Card className="bg-blue-50 border-2 border-blue-200">
                <div className="p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-500 p-2 rounded-lg flex-shrink-0">
                      <span className="text-xl sm:text-2xl">ℹ️</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 mb-2 text-sm">Information</h3>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        Les données se mettent à jour automatiquement toutes les 30 secondes et instantanément via les notifications en temps réel.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm border-2 border-gray-200 shadow-xl">
            <div className="text-center py-12 sm:py-16">
              <div className="bg-gradient-to-r from-gray-200 to-gray-300 w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 animate-pulse">
                <span className="text-6xl sm:text-7xl">🔗</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
                Aucune association assignée
              </h3>
              <p className="text-gray-600 text-base sm:text-lg max-w-md mx-auto px-4">
                Contactez votre Owner pour qu'il vous assigne une association
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SimpleDashboard;